import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RTLWrapper from "../components/RTLWrapper";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress, findProgressByEmail, migrateUserProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const arabicLetters = [
  { letter: "ا", pronunciation: "Alif" },
  { letter: "ب", pronunciation: "Ba" },
  { letter: "ت", pronunciation: "Ta" },
  { letter: "ث", pronunciation: "Tha" },
  { letter: "ج", pronunciation: "Jeem" },
  { letter: "ح", pronunciation: "Ha" },
  { letter: "خ", pronunciation: "Kha" },
  { letter: "د", pronunciation: "Dal" },
  { letter: "ذ", pronunciation: "Dhal" },
  { letter: "ر", pronunciation: "Ra" },
  { letter: "ز", pronunciation: "Zay" },
  { letter: "س", pronunciation: "Seen" },
  { letter: "ش", pronunciation: "Sheen" },
  { letter: "ص", pronunciation: "Sad" },
  { letter: "ض", pronunciation: "Dad" },
  { letter: "ط", pronunciation: "Taa" },
  { letter: "ظ", pronunciation: "Zaa" },
  { letter: "ع", pronunciation: "Ain" },
  { letter: "غ", pronunciation: "Ghain" },
  { letter: "ف", pronunciation: "Fa" },
  { letter: "ق", pronunciation: "Qaf" },
  { letter: "ك", pronunciation: "Kaf" },
  { letter: "ل", pronunciation: "Lam" },
  { letter: "م", pronunciation: "Meem" },
  { letter: "ن", pronunciation: "Noon" },
  { letter: "هـ", pronunciation: "Ha" },
  { letter: "و", pronunciation: "Waw" },
  { letter: "ي", pronunciation: "Ya" },
];

function AlphabetLesson() {
  const { user } = useUser();
  const [letters, setLetters] = useState(arabicLetters);
  const [selectedPronunciation, setSelectedPronunciation] = useState("");
  const [clickedLetters, setClickedLetters] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = async () => {
      if (user) {
        console.log('Loading progress for user:', user.id, 'email:', user.primaryEmailAddress?.emailAddress);
        
        // First, try to get progress by current user ID
        let userProgress = getUserProgress(user.id);
        
        // If no progress found and user has email, try to find by email
        if ((!userProgress.alphabetLetters || userProgress.alphabetLetters.length === 0) && 
            user.primaryEmailAddress?.emailAddress) {
          const existingProgress = findProgressByEmail(user.primaryEmailAddress.emailAddress);
          if (existingProgress) {
            console.log('Found existing progress by email, migrating...');
            migrateUserProgress(existingProgress.userId, user.id);
            userProgress = getUserProgress(user.id);
          }
        }
        
        if (Array.isArray(userProgress.alphabetLetters)) {
          setClickedLetters(new Set(userProgress.alphabetLetters));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (letter, pronunciation) => {
    setSelectedPronunciation(pronunciation);
    // Always create a new Set for React state
    const updated = new Set(clickedLetters);
    updated.add(letter);
    setClickedLetters(new Set(updated));

    // Save progress to database
    if (user) {
      try {
        const alphabetLetters = Array.from(updated);
        const progressData = {
          alphabetLetters: alphabetLetters,
          alphabet: alphabetLetters.length,
          email: user.primaryEmailAddress?.emailAddress
        };
        await updateLessonProgress(user.id, 'alphabet', alphabetLetters);
        // Also save email for future identification
        if (user.primaryEmailAddress?.emailAddress) {
          const { saveUserProgress } = await import('../data/database');
          await saveUserProgress(user.id, { email: user.primaryEmailAddress.emailAddress });
        }
        console.log('Progress saved to database:', alphabetLetters);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(letter, "Arabic Male");
  };

  const shuffleLetters = () => {
    const shuffled = [...letters].sort(() => Math.random() - 0.5);
    setLetters(shuffled);
    setSelectedPronunciation("");
  };

  const resetLetters = async () => {
    setLetters(arabicLetters);
    setSelectedPronunciation("");
    setClickedLetters(new Set());
    // Reset progress in database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'alphabet', []);
        console.log('Progress reset successfully in database');
      } catch (error) {
        console.error('Error resetting progress:', error);
      }
    }
  };

  const progress = (clickedLetters.size / arabicLetters.length) * 100;

  return (
    <RTLWrapper>
      <div className="p-4 sm:p-6 text-center h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-y-auto">
        {/* Modern background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
        
        <h2 className="text-3xl sm:text-4xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent relative z-10">الحروف العربية</h2>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto bg-white/60 backdrop-blur-sm rounded-full h-6 mb-4 overflow-hidden shadow-lg border border-white/30 relative z-10">
          <motion.div
            className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <p className="text-sm text-gray-600 mb-6 relative z-10">
          تم تعلم {clickedLetters.size} من 28 حرف
        </p>
        {/* Pronunciation Display */}
        <AnimatePresence>
          {selectedPronunciation && (
            <motion.div
              key="pronunciation"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
              className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-6 relative z-10"
            >
              النطق: {selectedPronunciation}
            </motion.div>
          )}
        </AnimatePresence>
        {/* Letters Grid */}
        <div dir="rtl" className="grid grid-cols-4 sm:grid-cols-6 gap-4 justify-center mb-6 relative z-10">
          {letters.map(({ letter, pronunciation }, index) => {
            const learned = clickedLetters.has(letter);
            return (
              <button
                key={index}
                onClick={() => handleClick(letter, pronunciation)}
                className={`text-3xl sm:text-4xl font-bold p-5 rounded-2xl shadow-lg transition-all duration-300 hover:scale-105 ${
                  learned
                    ? "bg-gradient-to-br from-green-100 to-emerald-100 text-green-700 border border-green-200 shadow-green-200/50"
                    : "bg-white/80 backdrop-blur-sm text-blue-700 hover:bg-blue-50 border border-white/30 hover:shadow-xl"
                }`}
                title={pronunciation}
              >
                {letter}
              </button>
            );
          })}
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center relative z-10">
          <button
            onClick={resetLetters}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-lg px-6 py-3 rounded-full shadow-lg transition-all duration-300 w-64 sm:w-auto hover:scale-105 hover:shadow-xl"
          >
            إعادة الترتيب - Reset
          </button>
          <button
            onClick={shuffleLetters}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-lg px-6 py-3 rounded-full shadow-lg transition-all duration-300 w-64 sm:w-auto hover:scale-105 hover:shadow-xl"
          >
            خلط الحروف - Shuffle
          </button>
          <SaveButton
            lessonType="alphabet"
            progressData={Array.from(clickedLetters)}
            className="w-64 sm:w-auto"
          />
        </div>

        {/* ResponsiveVoice Script */}
        <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
      </div>
    </RTLWrapper>
  );
}

export default AlphabetLesson;
